import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Button, FormControlLabel, Stack, Switch, TextField } from '@mui/material';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { lowerCase, upperFirst } from 'lodash';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import useWizardStepContext from 'v2/components/WizardModal/useWizardStepContext';
import useExternalUsers from 'v2/hooks/useExternalUsers/useExternalUsers';

export const CREATE_GROUP_STEP_2_FORM_ID = 'create-group-step-2';
const STEP_INDEX = 1;

const DEFAULT_MAX_MEMBERS = 10;
const MIN_MAX_MEMBERS = 1;
const MAX_MAX_MEMBERS = 999;

interface FormValues {
  isMaximumUserLimit: boolean;
  maxNumberOfMembers: number;
}

interface Props {
  groupId: string | null;
  groupName: string | null;
}

const GroupSettingsStep: React.FC<Props> = ({ groupId, groupName }) => {
  const { translation } = useTranslation();
  const { showSnackbar } = useSnackbarContext();
  const { goToNext, setFooterContent, steps } = useWizardStepContext();

  const { updateVisitorGroupWithMaxNumberOfMembers, updateVisitorGroupWithMaxNumberOfMembersMutationLoading } =
    useExternalUsers({ translation, handleFetchError: (msg) => showSnackbar(msg, { severity: 'error' }) });

  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors }
  } = useForm<FormValues>({
    mode: 'onTouched',
    defaultValues: { isMaximumUserLimit: false, maxNumberOfMembers: DEFAULT_MAX_MEMBERS }
  });

  const isMaximumUserLimitEnabled = watch('isMaximumUserLimit');

  const onSubmit = handleSubmit(async (data) => {
    try {
      if (groupId && groupName) {
        await updateVisitorGroupWithMaxNumberOfMembers(
          groupName,
          groupId,
          data.isMaximumUserLimit ? data.maxNumberOfMembers : null
        );
      }
      goToNext();
    } catch {
      // Error displayed via snackbar in useExternalUsers
    }
  });

  React.useLayoutEffect(() => {
    setFooterContent(
      STEP_INDEX,
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          key="group-setting-step-submit"
          id="group-settings-next-button"
          variant="contained"
          type="submit"
          form={CREATE_GROUP_STEP_2_FORM_ID}
          loading={updateVisitorGroupWithMaxNumberOfMembersMutationLoading}
          endIcon={<ChevronRightIcon />}
        >
          {steps[STEP_INDEX + 1]?.label}
        </Button>
      </Box>
    );
  }, [updateVisitorGroupWithMaxNumberOfMembersMutationLoading, setFooterContent, steps, translation.back]);

  return (
    <Stack component="form" id={CREATE_GROUP_STEP_2_FORM_ID} onSubmit={onSubmit} noValidate gap={3}>
      <Controller
        name="isMaximumUserLimit"
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={
              <Switch id="isMaximumUserLimit-switch" checked={field.value} onChange={field.onChange} color="primary" />
            }
            label={translation.enable_maximum_user_limit}
          />
        )}
      />
      <TextField
        {...register('maxNumberOfMembers', {
          valueAsNumber: true,
          validate: (value) => {
            if (!isMaximumUserLimitEnabled) return true;
            if (!Number.isInteger(value)) return translation.maximum_users_whole_number;
            if (!value || value < MIN_MAX_MEMBERS) return translation.maximum_users_cannot_be_empty;
            return true;
          }
        })}
        id="maxNumberOfMembers-input"
        label={upperFirst(lowerCase(translation.maximum_users))}
        type="number"
        variant="outlined"
        disabled={!isMaximumUserLimitEnabled}
        slotProps={{ htmlInput: { min: MIN_MAX_MEMBERS, max: MAX_MAX_MEMBERS, step: 1 } }}
        error={!!errors.maxNumberOfMembers}
        helperText={errors.maxNumberOfMembers?.message ?? ' '}
        sx={{
          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
            WebkitAppearance: 'none',
            margin: 0
          },
          '& input[type=number]': { MozAppearance: 'textfield' }
        }}
      />
    </Stack>
  );
};

export default GroupSettingsStep;
